import React, { useState, useEffect, useRef, useMemo } from "react";
import MasterLayout from "../../masterLayout/MasterLayout";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import Select from "react-select";
import { Icon } from "@iconify/react";
import esLocale from "@fullcalendar/core/locales/es";
import "./Calendario.css";
import Loader from "../../components/Loader";
import useCalendarioStore from "../../store/calendarioStore";
import CanchasParrilla from "./CanchasParrilla";

const CalendarioPage = () => {
  const { isLoading, error, partidas, sucursales, bloqueos, canchas, clientes, sucursalSeleccionada, setSucursalSeleccionada, cargarPartidas, iniciar, crearBloqueo, crearReservacion, cancelarReservacion, eliminarBloqueo, aprobarPartida, recordarPagoPartida, cargarChatPartida, enviarChatPartida } = useCalendarioStore();
  const [initialView, setInitialView] = useState("parrilla");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarTitle, setCalendarTitle] = useState("");
  const visibleDate = useRef(new Date());
  const calendarRef = useRef(null);

  // Auto-seleccionar la primera sucursal si no hay ninguna seleccionada
  useEffect(() => {
    if (sucursales.length > 0 && (!sucursalSeleccionada || sucursalSeleccionada.value === -1)) {
      setSucursalSeleccionada({ value: sucursales[0].id, label: sucursales[0].nombre });
    }
  }, [sucursales, sucursalSeleccionada, setSucursalSeleccionada]);

  // Estados para modal de reserva/bloqueo
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accionCalendario, setAccionCalendario] = useState("reservar");
  const [fechaBloqueo, setFechaBloqueo] = useState("");
  const [sucursalBloqueo, setSucursalBloqueo] = useState(null);
  const [canchaBloqueo, setCanchaBloqueo] = useState(null);
  const [jugadorSeleccionado, setJugadorSeleccionado] = useState(null);
  const [tipoBloqueo, setTipoBloqueo] = useState("dia"); // "dia" o "rango"
  const [horaInicio, setHoraInicio] = useState("07:00");
  const [horaFin, setHoraFin] = useState("23:00");
  const [motivo, setMotivo] = useState("");
  const [limiteJugadores, setLimiteJugadores] = useState("4");
  const [guardandoBloqueo, setGuardandoBloqueo] = useState(false);

  // Estados para modal de aprobación
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [selectedPartida, setSelectedPartida] = useState(null);
  const [selectedJugador, setSelectedJugador] = useState(null);
  const [procesandoAprobacion, setProcesandoAprobacion] = useState(false);
  const [cancelandoReservacion, setCancelandoReservacion] = useState(false);
  const [enviandoRecordatorioPago, setEnviandoRecordatorioPago] = useState(false);
  const [chatStaffOpen, setChatStaffOpen] = useState(false);
  const [chatStaffLoading, setChatStaffLoading] = useState(false);
  const [chatStaffSending, setChatStaffSending] = useState(false);
  const [chatStaffMessages, setChatStaffMessages] = useState([]);
  const [chatStaffText, setChatStaffText] = useState("");

  // Estado para filtrado de canchas
  const [canchaFiltroSeleccionada, setCanchaFiltroSeleccionada] = useState(null);

  useEffect(() => {
    iniciar();
  }, [iniciar]);

  // Canchas dinámicas basadas en la sucursal seleccionada en el formulario
  const canchaOptions = useMemo(() => {
    console.log("[Calendario Component] Recalculando canchaOptions...");
    console.log("[Calendario Component] Canchas del Store:", canchas);
    console.log("[Calendario Component] Sucursal del modal para bloqueo:", sucursalBloqueo);

    if (!sucursalBloqueo || sucursalBloqueo.value === -1) {
      return [{ value: "global", label: "Todas las canchas (Global)" }];
    }
    const filtered = canchas
      .filter((c) => {
        console.log(`[Calendario Component] Comparando cancha branchId: ${c.branchId} (tipo: ${typeof c.branchId}) con sucursal seleccionada value: ${sucursalBloqueo.value} (tipo: ${typeof sucursalBloqueo.value})`);
        return c.branchId === sucursalBloqueo.value;
      })
      .map((c) => ({ value: c.id, label: `${c.nombre} #N/A` }));

    return [
      { value: "sucursal", label: "Todas las canchas de esta sucursal" },
      ...filtered
    ];
  }, [sucursalBloqueo, canchas]);

  // Opciones de canchas para el filtro del calendario
  const canchasFilterOptions = useMemo(() => {
    if (!sucursalSeleccionada || sucursalSeleccionada.value === -1) {
      return [];
    }
    return canchas
      .filter((c) => c.branchId === sucursalSeleccionada.value)
      .map((c) => ({ value: c.id, label: `${c.nombre} #N/A` }));
  }, [sucursalSeleccionada, canchas]);

  // Opciones de jugadores/clientes para el buscador en la reserva
  const jugadorOptions = useMemo(() => {
    return (clientes || []).map((cliente) => {
      const nombreCompleto = `${cliente.nombre || ""} ${cliente.apellido || ""}`.trim() || "Sin nombre";
      const email = cliente.email || "";
      const tel = "+" + cliente.telefono || "";
      const extraInfo = [email, tel].filter(Boolean).join(" • ");
      return {
        value: cliente.id,
        label: extraInfo ? `${nombreCompleto} (${extraInfo})` : nombreCompleto,
        cliente,
      };
    });
  }, [clientes]);

  const abrirModalCalendario = ({ accion = "reservar", fecha = "", hora = "", horaInicio: hInicioParam = "", horaFin: hFinParam = "", cancha = null } = {}) => {
    const nextSucursal = sucursalSeleccionada && sucursalSeleccionada.value !== -1 ? sucursalSeleccionada : null;
    const preselectedCourtOption = cancha
      ? { value: cancha.id, label: `${cancha.nombre} #N/A` }
      : (canchaFiltroSeleccionada || null);

    const effectiveHoraInicio = hInicioParam || hora || "07:00";
    setAccionCalendario(accion);
    setFechaBloqueo(fecha || (selectedDate ? selectedDate.toISOString().split("T")[0] : ""));
    setSucursalBloqueo(nextSucursal);
    setCanchaBloqueo(preselectedCourtOption);
    setJugadorSeleccionado(null);
    setTipoBloqueo("rango");
    setHoraInicio(effectiveHoraInicio);

    if (hFinParam) {
      setHoraFin(hFinParam);
    } else {
      const courtIdToUse = preselectedCourtOption?.value;
      const selectedCourt = canchas.find((c) => c.id === courtIdToUse);
      const duration = Number(selectedCourt?.tiempoUso || 90);
      const [hour, minute] = effectiveHoraInicio.split(":").map(Number);
      const end = new Date(2000, 0, 1, hour || 7, minute || 0);
      end.setMinutes(end.getMinutes() + duration);
      setHoraFin(`${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`);
    }

    setMotivo(accion === "reservar" ? "Reservación desde calendario" : "");
    setLimiteJugadores("4");
    setIsModalOpen(true);
  };

  // Resetear la cancha seleccionada si cambia la sucursal del modal a una que no corresponda
  useEffect(() => {
    if (canchaBloqueo && sucursalBloqueo && sucursalBloqueo.value !== -1) {
      const match = canchas.find((c) => c.id === canchaBloqueo.value);
      if (match && match.branchId !== sucursalBloqueo.value) {
        setCanchaBloqueo(null);
      }
    }
  }, [sucursalBloqueo, canchaBloqueo, canchas]);

  const handleSucursalChange = async (selectedOption) => {
    setSucursalSeleccionada(selectedOption);
    setCanchaFiltroSeleccionada(null); // Limpiar el filtro de cancha al cambiar de sucursal
    const idSucursal = selectedOption ? selectedOption.value : -1;
    await cargarPartidas(idSucursal);
  };

  const formatTimeFromDB = (timeString) => {
    if (!timeString) return "";
    if (typeof timeString === "string") {
      const match = timeString.match(/(\d{1,2}):(\d{2}):(\d{2})/);
      if (match) {
        return `${match[1].padStart(2, "0")}:${match[2]}`;
      }
    }
    return timeString;
  };

  const getColorByLevel = (nivel) => {
    if (!nivel) return "#63487b";
    const nivelStr = String(nivel).toLowerCase();
    if (nivelStr.includes("varonil") || nivelStr.includes("masculino")) return "#EA6852";
    if (nivelStr.includes("femenil") || nivelStr.includes("femenino")) return "#9b59b6";
    if (nivelStr.includes("mixto")) return "#3498db";
    return "#63487b";
  };

  const nombreJugador = (jugador) => {
    if (!jugador) return "Jugador sin nombre";
    return `${jugador.nombre || ""} ${jugador.apellido || ""}`.trim() || jugador.name || jugador.nombre || "Jugador sin nombre";
  };

  const abrirExpedienteJugador = (jugador, extra = {}) => {
    if (!jugador) return;
    setSelectedJugador({
      ...jugador,
      ...extra,
      nombreCompleto: nombreJugador(jugador),
    });
  };

  const abrirChatStaff = async () => {
    if (!selectedPartida?.idPartida) return;
    setChatStaffOpen(true);
    setChatStaffLoading(true);
    const response = await cargarChatPartida(selectedPartida.idPartida);
    if (response.status) {
      setChatStaffMessages(Array.isArray(response.data) ? response.data : []);
    } else {
      alert(response.message || "No se pudo cargar el chat de la partida.");
    }
    setChatStaffLoading(false);
  };

  const enviarMensajeStaff = async () => {
    const text = chatStaffText.trim();
    if (!selectedPartida?.idPartida || !text || chatStaffSending) return;
    setChatStaffSending(true);
    const response = await enviarChatPartida(selectedPartida.idPartida, text);
    if (response.status) {
      setChatStaffText("");
      const reload = await cargarChatPartida(selectedPartida.idPartida);
      setChatStaffMessages(Array.isArray(reload.data) ? reload.data : []);
    } else {
      alert(response.message || "No se pudo enviar el mensaje.");
    }
    setChatStaffSending(false);
  };

  const eventos = useMemo(() => {
    // 1. Mapear Partidas
    const partidasEvents = partidas.filter((partida) => partida.status !== 'cancelled').map((partida) => {
      const fechaISO = partida.fecha ? new Date(partida.fecha) : null;
      const horaInicio = fechaISO ? fechaISO.toTimeString().slice(0, 5) : "";
      const horaFin = fechaISO
        ? new Date(new Date(partida.fecha).getTime() + (partida.duracion || 0) * 60000).toTimeString().slice(0, 5)
        : "";
      const startDate = fechaISO || null;
      const endDate = fechaISO
        ? new Date(fechaISO.getTime() + (partida.duracion || 0) * 60000)
        : null;

      return {
        title: `${partida.court?.nombre || "Cancha"} - ${horaInicio} a ${horaFin}`,
        start: startDate,
        end: endDate,
        allDay: false,
        extendedProps: {
          esBloqueo: false,
          idPartida: partida.id,
          sucursal: partida.branch?.nombre || "Sucursal",
          cancha: partida.court?.nombre || "Cancha",
          numeroCancha: "",
          idCancha: partida.courtId,
          horaInicio,
          horaFin,
          nivel: partida.formato || "No especificado",
          categoria: "No especificada",
          maxJugadores: partida.limiteJugadores,
          currentPlayers: (partida.jugadores || []).length,
          creador: partida.creador || null,
          jugadores: Array.isArray(partida.jugadores) ? partida.jugadores : [],
          approved: partida.approvalStatus === "approved",
          canCancel:
            partida.matchType === 'admin_reservation' ||
            (partida.matchType === 'mobile_social' && partida.approvalStatus === 'pending'),
        },
        backgroundColor: partida.approvalStatus === "approved" ? "#d4edda" : "#e8f4fd",
        borderColor: partida.approvalStatus === "approved" ? "#28a745" : "#3b82f6",
      };
    }).filter(e => e.start !== null);

    // 2. Mapear Bloqueos
    const bloqueosEvents = bloqueos.map((bloqueo) => {
      const horaInicio = formatTimeFromDB(bloqueo.horaInicio);
      const horaFin = formatTimeFromDB(bloqueo.horaFin);
      let fecha = bloqueo.fecha;

      if (typeof fecha === "string") {
        fecha = fecha.split("T")[0];
      }

      const startDate = fecha && horaInicio ? `${fecha}T${horaInicio}:00` : null;
      const endDate = fecha && horaFin ? `${fecha}T${horaFin}:00` : null;

      const canchaLabel = bloqueo.courtId
        ? `${bloqueo.court?.nombre || "Cancha"} #`
        : "Todas las canchas (Global)";

      const sucursalLabel = bloqueo.branch?.nombre || "Todas las sucursales";

      return {
        title: `Horario bloqueado: ${bloqueo.motivo} (${canchaLabel})`,
        start: startDate,
        end: endDate,
        allDay: false,
        extendedProps: {
          esBloqueo: true,
          idBloqueo: bloqueo.id,
          motivo: bloqueo.motivo,
          sucursal: sucursalLabel,
          cancha: canchaLabel,
          idCancha: bloqueo.courtId,
          horaInicio,
          horaFin,
        },
        backgroundColor: "#7f8c8d",
        borderColor: "#7f8c8d",
      };
    }).filter(e => e.start !== null);

    // 3. Si hay filtro de sucursal en el calendario superior, filtrar eventos
    let resultado = [...partidasEvents, ...bloqueosEvents];
    if (sucursalSeleccionada && sucursalSeleccionada.value !== -1) {
      resultado = resultado.filter((e) => {
        if (e.extendedProps.esBloqueo) {
          // Bloqueos de esta sucursal o bloqueos globales
          return e.extendedProps.sucursal === sucursalSeleccionada.label || e.extendedProps.sucursal === "Todas las sucursales";
        } else {
          return e.extendedProps.sucursal === sucursalSeleccionada.label;
        }
      });
    }

    // 4. Si hay filtro de cancha en el calendario superior, filtrar eventos
    if (canchaFiltroSeleccionada) {
      resultado = resultado.filter((e) => {
        if (e.extendedProps.esBloqueo) {
          // Mostrar bloqueos que sean específicos de esta cancha, o globales de la sucursal/generales
          return e.extendedProps.idCancha === canchaFiltroSeleccionada.value || !e.extendedProps.idCancha;
        } else {
          return e.extendedProps.idCancha === canchaFiltroSeleccionada.value;
        }
      });
    }

    return resultado;
  }, [partidas, bloqueos, sucursalSeleccionada, canchas, canchaFiltroSeleccionada]);

  const renderEventContent = (eventInfo) => {
    const { esBloqueo, sucursal, cancha, numeroCancha, horaInicio, horaFin, nivel, motivo, approved } = eventInfo.event.extendedProps;
    
    if (esBloqueo) {
      return (
        <div className="fc-event-custom-content p-1" style={{ borderLeft: "1px solid #c0392b" }}>
          <div className="fw-bold text-wrap" style={{ color: "#c0392b" }}>Bloqueado: {cancha}</div>
          <div style={{ fontSize: "11px", color: "#1a202c", fontWeight: "600" }}>
            {horaInicio} - {horaFin}
          </div>
          <div className="text-wrap" style={{ fontSize: "10px", fontStyle: "italic", color: "#4a5568", fontWeight: "500" }}>
            Motivo: {motivo}
          </div>
        </div>
      );
    }

    const isApproved = approved;
    const borderLeftColor = isApproved ? "#28a745" : "#3b82f6";
    const titleColor = isApproved ? "#155724" : "#1d4ed8";
    const timeColor = isApproved ? "#155724" : "#1e40af";
    const footerColor = isApproved ? "#2e7d32" : "#2563eb";

    return (
      <div className="fc-event-custom-content p-1 w-100 h-100" style={{ borderLeft: `1px solid ${borderLeftColor}` }}>
        <div className="fw-bold text-wrap" style={{ color: titleColor }}>{cancha} #{numeroCancha}</div>
        <div style={{ fontSize: "11px", color: timeColor, fontWeight: "600" }}>
          {horaInicio} - {horaFin}
        </div>
        <div className="text-wrap" style={{ fontSize: "10px", opacity: 0.9, color: footerColor, fontWeight: "500" }}>
          {sucursal} | {isApproved ? "✓ Aprobada" : "⏳ Pendiente"}
        </div>
      </div>
    );
  };

  const handleGuardarBloqueo = async (e) => {
    e.preventDefault();
    if (!fechaBloqueo || !motivo) {
      alert("Por favor completa los campos obligatorios.");
      return;
    }
    if (accionCalendario === "reservar" && (!sucursalBloqueo || !canchaBloqueo || ["sucursal", "global"].includes(canchaBloqueo.value))) {
      alert("Para reservar selecciona una sucursal y una cancha especifica.");
      return;
    }

    setGuardandoBloqueo(true);
    try {
      if (accionCalendario === "reservar") {
        const fecha = `${fechaBloqueo}T${horaInicio}:00`;
        const inicio = new Date(fecha);
        const [finHora, finMinuto] = horaFin.split(":").map(Number);
        const fin = new Date(inicio);
        fin.setHours(finHora || 0, finMinuto || 0, 0, 0);
        const duracion = Math.max(15, Math.round((fin.getTime() - inicio.getTime()) / 60000));
        const finalTitulo = motivo || (jugadorSeleccionado ? `Reserva: ${jugadorSeleccionado.label}` : "Reservación");
        const res = await crearReservacion({
          titulo: finalTitulo,
          formato: "Reservacion",
          fecha,
          duracion,
          branchId: sucursalBloqueo.value,
          courtId: canchaBloqueo.value,
          courtIds: [canchaBloqueo.value],
          limiteJugadores: Number(limiteJugadores) || 4,
          userId: jugadorSeleccionado?.value || null,
          jugadores: jugadorSeleccionado?.cliente ? [{
            id: jugadorSeleccionado.cliente.id,
            userId: jugadorSeleccionado.cliente.id,
            nombre: jugadorSeleccionado.cliente.nombre,
            apellido: jugadorSeleccionado.cliente.apellido,
            email: jugadorSeleccionado.cliente.email,
            telefono: jugadorSeleccionado.cliente.telefono,
          }] : [],
        });
        if (res.status) {
          alert(res.message || "Reservacion creada correctamente.");
          setIsModalOpen(false);
          setJugadorSeleccionado(null);
          iniciar();
        } else {
          alert("Error al crear reservacion: " + res.message);
        }
        return;
      }

      const payload = {
        fecha: fechaBloqueo,
        horaInicio: tipoBloqueo === "dia" ? "07:00" : horaInicio,
        horaFin: tipoBloqueo === "dia" ? "23:00" : horaFin,
        motivo,
        branchId: sucursalBloqueo && sucursalBloqueo.value !== -1 ? sucursalBloqueo.value : null,
        courtId: canchaBloqueo && canchaBloqueo.value !== "sucursal" && canchaBloqueo.value !== "global"
          ? canchaBloqueo.value
          : null,
        bloquearSucursal: canchaBloqueo && canchaBloqueo.value === "sucursal"
      };

      const res = await crearBloqueo(payload);
      if (res.status) {
        alert(res.message || "Bloqueo programado correctamente.");
        setIsModalOpen(false);
        setFechaBloqueo("");
        setSucursalBloqueo(null);
        setCanchaBloqueo(null);
        setTipoBloqueo("dia");
        setHoraInicio("07:00");
        setHoraFin("23:00");
        setMotivo("");
        setLimiteJugadores("4");
        iniciar(); // recargar
      } else {
        alert("Error al programar bloqueo: " + res.message);
      }
    } catch (error) {
      console.error(error);
      alert("Ocurrió un error al guardar el bloqueo.");
    } finally {
      setGuardandoBloqueo(false);
    }
  };

  const sucursalesOptions = [{ value: -1, label: "Todas las sucursales" }, ...sucursales.map((s) => ({ value: s.id, label: s.nombre }))];

  return (
    <MasterLayout>
      <div className="calendar-workspace mb-4">
        <div className="calendar-heading">
          <div className="calendar-heading-top">
            <div className="calendar-page-title" role="heading" aria-level="1">Calendario</div>
            <div className="calendar-actions">
              <button type="button" className="btn btn-success" onClick={() => abrirModalCalendario({ accion: "reservar" })}>
                <Icon icon="mingcute:add-line" /> Reservar
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => abrirModalCalendario({ accion: "bloquear" })}>
                <Icon icon="mingcute:lock-line" /> Bloquear horario
              </button>
            </div>
          </div>
          <div className="calendar-filters">
            <div className="calendar-filter">
              <label htmlFor="calendar-branch">Sucursal</label>
              <Select
                inputId="calendar-branch"
                options={sucursalesOptions}
                value={sucursalSeleccionada}
                onChange={handleSucursalChange}
                placeholder="Todas las sucursales"
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  container: (provided) => ({ ...provided, zIndex: 1000 }),
                }}
              />
            </div>
            <div className="calendar-filter">
              <label htmlFor="calendar-court">Cancha</label>
              <Select
                inputId="calendar-court"
                options={canchasFilterOptions}
                value={canchaFiltroSeleccionada}
                onChange={setCanchaFiltroSeleccionada}
                placeholder={
                  !sucursalSeleccionada || sucursalSeleccionada.value === -1
                    ? "Selecciona sucursal..."
                    : "Todas las canchas"
                }
                isDisabled={!sucursalSeleccionada || sucursalSeleccionada.value === -1}
                isClearable
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  container: (provided) => ({ ...provided, zIndex: 1000 }),
                }}
              />
            </div>
          </div>
          <div className="calendar-navigation">
            <div className="calendar-date-controls">
              <button
                type="button"
                disabled={isLoading}
                title="Periodo anterior"
                aria-label="Periodo anterior"
                onClick={() => {
                  if (initialView === "parrilla") {
                    const prev = new Date(selectedDate);
                    prev.setDate(prev.getDate() - 1);
                    setSelectedDate(prev);
                  } else {
                    calendarRef.current?.getApi().prev();
                  }
                }}
              >
                <Icon icon="mingcute:left-line" />
              </button>
              <button
                type="button"
                disabled={isLoading}
                title="Periodo siguiente"
                aria-label="Periodo siguiente"
                onClick={() => {
                  if (initialView === "parrilla") {
                    const next = new Date(selectedDate);
                    next.setDate(next.getDate() + 1);
                    setSelectedDate(next);
                  } else {
                    calendarRef.current?.getApi().next();
                  }
                }}
              >
                <Icon icon="mingcute:right-line" />
              </button>
              <button
                type="button"
                disabled={isLoading}
                onClick={() => {
                  const today = new Date();
                  setSelectedDate(today);
                  if (initialView !== "parrilla") {
                    calendarRef.current?.getApi().today();
                  }
                }}
              >
                Hoy
              </button>
            </div>
            <div className="calendar-period-title" role="heading" aria-level="2" aria-live="polite">
              {initialView === "parrilla" ? (
                selectedDate.toLocaleDateString("es-ES", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              ) : (
                calendarTitle || "Calendario"
              )}
            </div>
            <div className="calendar-views" role="group" aria-label="Vista del calendario">
              <button
                type="button"
                disabled={isLoading}
                aria-pressed={initialView === "parrilla"}
                onClick={() => setInitialView("parrilla")}
              >
                Canchas (Hoy)
              </button>
              {[["dayGridMonth", "Mes"], ["timeGridWeek", "Semana"], ["timeGridDay", "Día"], ["listWeek", "Lista"]].map(([view, label]) => (
                <button
                  type="button"
                  key={view}
                  disabled={isLoading}
                  aria-pressed={initialView === view}
                  onClick={() => {
                    setInitialView(view);
                    setTimeout(() => {
                      calendarRef.current?.getApi().changeView(view);
                    }, 50);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="calendar-legend" aria-label="Estados de las reservas">
            <span><i className="calendar-dot-approved" /> Aprobada / Pagada</span>
            <span><i className="calendar-dot-pending" /> Sin pagar / Pendiente</span>
            <span><i className="calendar-dot-blocked" /> Bloqueado</span>
          </div>
        </div>
        <div className="calendar-body">
          { error && (
            <div className="alert alert-warning" role="alert">
              {error}
            </div>
          )}
          {isLoading ? (
            <Loader />
          ) : initialView === "parrilla" ? (
            <CanchasParrilla
              selectedDate={selectedDate}
              canchas={canchas}
              partidas={partidas}
              bloqueos={bloqueos}
              sucursalSeleccionada={sucursalSeleccionada}
              onSlotClick={({ cancha, hora, horaInicio, horaFin, fecha }) => {
                abrirModalCalendario({
                  accion: "reservar",
                  fecha,
                  hora,
                  horaInicio,
                  horaFin,
                  cancha,
                });
              }}
              onEventClick={(partida, cancha) => {
                const dateObj = new Date(partida.fecha);
                const horaInicio = dateObj.toTimeString().slice(0, 5);
                const duracion = partida.duracion || 90;
                const endDateObj = new Date(dateObj.getTime() + duracion * 60000);
                const horaFin = endDateObj.toTimeString().slice(0, 5);

                setSelectedPartida({
                  idPartida: partida.id,
                  cancha: cancha?.nombre || partida.court?.nombre || "Cancha",
                  numeroCancha: "",
                  sucursal: partida.branch?.nombre || "Sucursal",
                  horaInicio,
                  horaFin,
                  nivel: partida.formato || "No especificado",
                  categoria: "No especificada",
                  maxJugadores: partida.limiteJugadores,
                  currentPlayers: (partida.jugadores || []).length,
                  creador: partida.creador || null,
                  jugadores: Array.isArray(partida.jugadores) ? partida.jugadores : [],
                  approved: partida.approvalStatus === "approved",
                  canCancel:
                    partida.matchType === "admin_reservation" ||
                    (partida.matchType === "mobile_social" && partida.approvalStatus === "pending"),
                  fecha: typeof partida.fecha === "string" ? partida.fecha.split("T")[0] : "",
                });
                setIsApprovalModalOpen(true);
              }}
              onBlockClick={async (bloqueo, cancha) => {
                const horaInicio = formatTimeFromDB(bloqueo.horaInicio);
                const horaFin = formatTimeFromDB(bloqueo.horaFin);
                const canchaNombre = cancha?.nombre || bloqueo.court?.nombre || "Todas las canchas";
                const sucursalNombre = bloqueo.branch?.nombre || "Todas las sucursales";

                const confirmar = window.confirm(
                  `HORARIO BLOQUEADO\n\n` +
                  `Sucursal: ${sucursalNombre}\n` +
                  `Cancha: ${canchaNombre}\n` +
                  `Horario: ${horaInicio} - ${horaFin}\n` +
                  `Motivo: ${bloqueo.motivo || "Sin motivo"}\n\n` +
                  `¿Deseas liberar este horario para que pueda volver a reservarse?`
                );
                if (confirmar) {
                  const result = await eliminarBloqueo(bloqueo.id);
                  if (result.status) {
                    alert("Horario liberado correctamente.");
                    iniciar();
                  } else {
                    alert("Error al liberar horario: " + result.message);
                  }
                }
              }}
            />
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView={initialView}
              headerToolbar={false}
              datesSet={(info) => {
                setCalendarTitle(info.view.title);
                visibleDate.current = info.view.calendar.getDate();
              }}
              events={eventos}
              eventContent={renderEventContent}
              dateClick={(info) => {
                const [fecha, horaCompleta] = info.dateStr.split("T");
                const hora = horaCompleta ? horaCompleta.slice(0, 5) : "";
                abrirModalCalendario({ accion: "reservar", fecha, hora });
              }}
              height="auto"
              initialDate={selectedDate || visibleDate.current}
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              allDaySlot={false}
              nowIndicator={true}
              locale={esLocale}
              hiddenDays={ [0, 7] }
              eventClick={async (info) => {
                const { esBloqueo, idBloqueo, motivo, sucursal, cancha, numeroCancha, horaInicio, horaFin, nivel, categoria, maxJugadores, currentPlayers, creador, jugadores, approved, canCancel, idPartida } = info.event.extendedProps;
                
                if (esBloqueo) {
                  const confirmar = window.confirm(
                    `HORARIO BLOQUEADO\n\n` +
                    `Sucursal: ${sucursal}\n` +
                    `Cancha: ${cancha}\n` +
                    `Horario: ${horaInicio} - ${horaFin}\n` +
                    `Motivo: ${motivo}\n\n` +
                    `¿Deseas liberar este horario para que pueda volver a reservarse?`
                  );
                  if (confirmar) {
                    const result = await eliminarBloqueo(idBloqueo);
                    if (result.status) {
                      alert("Horario liberado correctamente.");
                      iniciar();
                    } else {
                      alert("Error al liberar horario: " + result.message);
                    }
                  }
                } else {
                  setSelectedPartida({
                    idPartida,
                    cancha,
                    numeroCancha,
                    sucursal,
                    horaInicio,
                    horaFin,
                    nivel,
                    categoria,
                    maxJugadores,
                    currentPlayers,
                    creador,
                    jugadores,
                    approved,
                    canCancel,
                    fecha: info.event.startStr ? info.event.startStr.split("T")[0] : ""
                  });
                  setIsApprovalModalOpen(true);
                }
              }}
            />
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 bg-base">
              <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0 d-flex justify-content-between align-items-center">
                <h5 className="modal-title fs-5 mb-0">
                  {accionCalendario === "reservar" ? "Reservar cancha" : "Bloquear horario"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close" style={{ border: 'none', background: 'transparent', fontSize: '20px' }}>&times;</button>
              </div>
              <form onSubmit={handleGuardarBloqueo}>
                <div className="modal-body p-24">
                  
                  <div className="mb-3">
                    <label className="form-label fw-bold">Fecha *</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      required 
                      value={fechaBloqueo}
                      onChange={(e) => setFechaBloqueo(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Sucursal</label>
                    <Select
                      options={[{ value: -1, label: "Todas las sucursales (Global)" }, ...sucursales.map(s => ({ value: s.id, label: s.nombre }))]}
                      value={sucursalBloqueo}
                      onChange={setSucursalBloqueo}
                      placeholder="Selecciona sucursal..."
                      isClearable
                      styles={{ container: (provided) => ({ ...provided, zIndex: 10000 }) }}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Cancha</label>
                    <Select
                      options={canchaOptions}
                      value={canchaBloqueo}
                      onChange={setCanchaBloqueo}
                      placeholder="Selecciona cancha..."
                      isClearable
                      styles={{ container: (provided) => ({ ...provided, zIndex: 9999 }) }}
                    />
                  </div>

                  {accionCalendario === "reservar" && (
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Jugador / Cliente
                        <span className="text-muted fw-normal ms-2" style={{ fontSize: "12px" }}>
                          (Busca por nombre, correo o teléfono)
                        </span>
                      </label>
                      <Select
                        options={jugadorOptions}
                        value={jugadorSeleccionado}
                        onChange={(selected) => {
                          setJugadorSeleccionado(selected);
                          if (selected?.cliente && !motivo) {
                            const nombre = `${selected.cliente.nombre || ""} ${selected.cliente.apellido || ""}`.trim();
                            setMotivo(`Reserva de ${nombre}`);
                          }
                        }}
                        placeholder="Escribe para buscar jugador (ej: Juan, correo@..., 81...)"
                        isClearable
                        isSearchable
                        noOptionsMessage={() => "No se encontraron jugadores registrados"}
                        styles={{ container: (provided) => ({ ...provided, zIndex: 9998 }) }}
                      />
                    </div>
                  )}

                  {accionCalendario === "bloquear" && (
                  <div className="mb-3">
                    <label className="form-label fw-bold d-block">Duración del Horario Reservado</label>
                    <div className="d-flex gap-4">
                      <div className="form-check">
                        <input 
                          className="form-check-input" 
                          type="radio" 
                          name="tipoBloqueo" 
                          id="bloqueoDia" 
                          checked={tipoBloqueo === "dia"}
                          onChange={() => setTipoBloqueo("dia")}
                        />
                        <label className="form-check-label" htmlFor="bloqueoDia">Todo el día (07:00 - 23:00)</label>
                      </div>
                      <div className="form-check">
                        <input 
                          className="form-check-input" 
                          type="radio" 
                          name="tipoBloqueo" 
                          id="bloqueoRango" 
                          checked={tipoBloqueo === "rango"}
                          onChange={() => setTipoBloqueo("rango")}
                        />
                        <label className="form-check-label" htmlFor="bloqueoRango">Rango de horas</label>
                      </div>
                    </div>
                  </div>
                  )}

                  {(accionCalendario === "reservar" || tipoBloqueo === "rango") && (
                    <div className="row mb-3">
                      <div className="col">
                        <label className="form-label fw-bold">Hora Inicio *</label>
                        <input 
                          type="time" 
                          className="form-control" 
                          value={horaInicio}
                          onChange={(e) => setHoraInicio(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col">
                        <label className="form-label fw-bold">Hora Fin *</label>
                        <input 
                          type="time" 
                          className="form-control" 
                          value={horaFin}
                          onChange={(e) => setHoraFin(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  )}

                  {accionCalendario === "reservar" && (
                    <div className="mb-3">
                      <label className="form-label fw-bold">Número de jugadores</label>
                      <select className="form-control" value={limiteJugadores} onChange={(e) => setLimiteJugadores(e.target.value)}>
                        <option value="2">2 jugadores</option>
                        <option value="4">4 jugadores</option>
                      </select>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-bold">{accionCalendario === "reservar" ? "Nombre o nota de la reservación *" : "Motivo del bloqueo *"}</label>
                    <textarea 
                      className="form-control" 
                      rows="3" 
                      required 
                      placeholder={accionCalendario === "reservar" ? "Ej: Reserva por WhatsApp, torneo, clase privada" : "Ej: Mantenimiento, evento privado, festivo"}
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value)}
                    ></textarea>
                  </div>

                </div>
                <div className="modal-footer border border-bottom-0 border-start-0 border-end-0 py-16 px-24">
                  <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                  <button type="submit" className={accionCalendario === "reservar" ? "btn btn-success" : "btn btn-danger"} disabled={guardandoBloqueo}>
                    {guardandoBloqueo ? "Guardando..." : (accionCalendario === "reservar" ? "Crear reservación" : "Bloquear horario")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {isApprovalModalOpen && selectedPartida && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 bg-base">
              <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0 d-flex justify-content-between align-items-center">
                <h5 className="modal-title fs-5 mb-0">
                  {selectedPartida.approved ? "✓ Detalle de Partida Aprobada" : "⏳ Aprobar Reservación de Partida"}
                </h5>
                <button type="button" className="btn-close" onClick={() => setIsApprovalModalOpen(false)} aria-label="Close" style={{ border: 'none', background: 'transparent', fontSize: '20px' }}>&times;</button>
              </div>
              <div className="modal-body p-24">
                <div className="mb-3">
                  <span className="fw-bold">Sucursal:</span> {selectedPartida.sucursal}
                </div>
                <div className="mb-3">
                  <span className="fw-bold">Cancha:</span> {selectedPartida.cancha} #{selectedPartida.numeroCancha}
                </div>
                <div className="mb-3">
                  <span className="fw-bold">Fecha:</span> {selectedPartida.fecha}
                </div>
                <div className="mb-3">
                  <span className="fw-bold">Horario:</span> {selectedPartida.horaInicio} - {selectedPartida.horaFin}
                </div>
                <div className="mb-3">
                  <span className="fw-bold">Nivel / Categoría:</span> {selectedPartida.nivel} / {selectedPartida.categoria}
                </div>
                <div className="mb-3">
                  <span className="fw-bold">Jugadores:</span> {selectedPartida.currentPlayers || 0} / {selectedPartida.maxJugadores}
                </div>
                <div className="mb-3">
                  <span className="fw-bold d-block mb-1">Creada por:</span>
                  {selectedPartida.creador ? (
                    <button
                      type="button"
                      className="border rounded px-3 py-2 bg-transparent text-start w-100"
                      onClick={() => abrirExpedienteJugador(selectedPartida.creador, { rolPartida: "Creador" })}
                    >
                      <div className="fw-semibold">
                        {nombreJugador(selectedPartida.creador)}
                      </div>
                      {selectedPartida.creador.email && (
                        <div className="text-muted small">{selectedPartida.creador.email}</div>
                      )}
                      {selectedPartida.creador.telefono && (
                        <div className="text-muted small">{selectedPartida.creador.telefono}</div>
                      )}
                    </button>
                  ) : (
                    <span className="text-muted">No especificado</span>
                  )}
                </div>
                <div className="mb-3">
                  <span className="fw-bold d-block mb-1">Jugadores unidos:</span>
                  {Array.isArray(selectedPartida.jugadores) && selectedPartida.jugadores.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {selectedPartida.jugadores.map((jugador, index) => (
                        <button
                          type="button"
                          key={jugador.userId || jugador.id || index}
                          className="border rounded px-3 py-2 d-flex justify-content-between align-items-center bg-transparent text-start w-100"
                          onClick={() => abrirExpedienteJugador(jugador, { rolPartida: jugador.role === "creator" ? "Creador" : "Jugador" })}
                        >
                          <span>
                            <span className="fw-semibold">{nombreJugador(jugador) || `Jugador ${index + 1}`}</span>
                            {(jugador.email || jugador.telefono) && (
                              <span className="d-block text-muted small">
                                {[jugador.email, jugador.telefono].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </span>
                          {selectedPartida.creador?.id && jugador.userId === selectedPartida.creador.id && (
                            <span className="badge bg-primary">Creador</span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">No hay jugadores unidos.</span>
                  )}
                </div>
                <div className="mb-3">
                  <span className="fw-bold">Estado:</span>{" "}
                  {selectedPartida.approved ? (
                    <span className="badge bg-success">Aprobada</span>
                  ) : (
                    <span className="badge bg-warning text-dark">Pendiente de Aprobación</span>
                  )}
                </div>
              </div>
              <div className="modal-footer border border-bottom-0 border-start-0 border-end-0 py-16 px-24">
                <button type="button" className="btn btn-secondary" onClick={() => setIsApprovalModalOpen(false)}>Cerrar</button>
                {selectedPartida.canCancel && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    disabled={cancelandoReservacion || procesandoAprobacion}
                    onClick={async () => {
                      if (!window.confirm('¿Cancelar esta reservación? El horario volverá a estar disponible.')) return;
                      setCancelandoReservacion(true);
                      try {
                        const res = await cancelarReservacion(selectedPartida.idPartida);
                        if (res.status) {
                          alert(res.message || 'Reservación cancelada correctamente.');
                          setIsApprovalModalOpen(false);
                          iniciar();
                        } else {
                          alert('Error al cancelar reservación: ' + (res.message || 'Intenta nuevamente.'));
                        }
                      } catch (error) {
                        console.error(error);
                        alert('Ocurrió un error al cancelar la reservación.');
                      } finally {
                        setCancelandoReservacion(false);
                      }
                    }}
                  >
                    {cancelandoReservacion ? 'Cancelando...' : 'Cancelar reservación'}
                  </button>
                )}
                {selectedPartida.creador?.id && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={async () => {
                      setEnviandoRecordatorioPago(true);
                      try {
                        const res = await recordarPagoPartida(selectedPartida.idPartida);
                        if (res.status) {
                          alert(res.message || "Recordatorio enviado correctamente.");
                        } else {
                          alert("Error al enviar recordatorio: " + (res.message || "Intenta nuevamente."));
                        }
                      } catch (error) {
                        console.error(error);
                        alert("Ocurrió un error al enviar el recordatorio.");
                      } finally {
                        setEnviandoRecordatorioPago(false);
                      }
                    }}
                    disabled={enviandoRecordatorioPago || procesandoAprobacion}
                  >
                    {enviandoRecordatorioPago ? "Enviando..." : "Recordar pago"}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={abrirChatStaff}
                >
                  Chat staff
                </button>
                {!selectedPartida.approved && (
                  <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={async () => {
                      setProcesandoAprobacion(true);
                      try {
                        const res = await aprobarPartida(selectedPartida.idPartida);
                        if (res.status) {
                          alert(res.message || "Partida aprobada correctamente.");
                          setIsApprovalModalOpen(false);
                          iniciar(); // recargar
                        } else {
                          alert("Error al aprobar partida: " + res.message);
                        }
                      } catch (error) {
                        console.error(error);
                        alert("Ocurrió un error al aprobar la partida.");
                      } finally {
                        setProcesandoAprobacion(false);
                      }
                    }}
                    disabled={procesandoAprobacion}
                  >
                    {procesandoAprobacion ? "Aprobando..." : "✓ Aprobar Reservación"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedJugador && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 bg-base">
              <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0 d-flex justify-content-between align-items-center">
                <h5 className="modal-title fs-5 mb-0">Expediente del jugador</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedJugador(null)} aria-label="Close" style={{ border: 'none', background: 'transparent', fontSize: '20px' }}>&times;</button>
              </div>
              <div className="modal-body p-24">
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                    style={{
                      width: 72,
                      height: 72,
                      backgroundColor: "#89A5CE",
                      backgroundImage: selectedJugador.photoUrl ? `url(${selectedJugador.photoUrl})` : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  >
                    {selectedJugador.photoUrl ? "" : selectedJugador.nombreCompleto?.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h5 className="mb-1">{selectedJugador.nombreCompleto}</h5>
                    <span className="badge bg-primary">{selectedJugador.rolPartida || "Jugador"}</span>
                  </div>
                </div>

                <div className="mb-3">
                  <span className="fw-bold d-block">ID jugador</span>
                  <span className="text-muted">{selectedJugador.userId || selectedJugador.id || "No disponible"}</span>
                </div>
                <div className="mb-3">
                  <span className="fw-bold d-block">Correo</span>
                  <span className="text-muted">{selectedJugador.email || "No disponible"}</span>
                </div>
                <div className="mb-3">
                  <span className="fw-bold d-block">Telefono</span>
                  <span className="text-muted">{selectedJugador.telefono || "No disponible"}</span>
                </div>
                {selectedJugador.joinedAt && (
                  <div className="mb-3">
                    <span className="fw-bold d-block">Se unio</span>
                    <span className="text-muted">{new Date(selectedJugador.joinedAt).toLocaleString("es-MX")}</span>
                  </div>
                )}
              </div>
              <div className="modal-footer border border-bottom-0 border-start-0 border-end-0 py-16 px-24">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedJugador(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {chatStaffOpen && selectedPartida && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content radius-16 bg-base">
              <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0 d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="modal-title fs-5 mb-0">Chat de partida</h5>
                  <div className="text-muted small">Vista staff · {selectedPartida.cancha} · {selectedPartida.horaInicio}</div>
                </div>
                <button type="button" className="btn-close" onClick={() => setChatStaffOpen(false)} aria-label="Close" style={{ border: 'none', background: 'transparent', fontSize: '20px' }}>&times;</button>
              </div>
              <div className="modal-body p-24">
                <div className="border rounded p-3" style={{ height: 320, overflowY: "auto", background: "#f8fafc" }}>
                  {chatStaffLoading ? (
                    <div className="text-center text-muted py-5">Cargando chat...</div>
                  ) : chatStaffMessages.length === 0 ? (
                    <div className="text-center text-muted py-5">Aun no hay mensajes en esta partida.</div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {chatStaffMessages.map((message) => {
                        const name = `${message.user?.nombre || ""} ${message.user?.apellido || ""}`.trim() || "Usuario";
                        return (
                          <div key={message.id} className="bg-white border rounded px-3 py-2">
                            <div className="d-flex justify-content-between gap-2">
                              <span className="fw-semibold">{name}</span>
                              <span className="text-muted small">
                                {message.createdAt ? new Date(message.createdAt).toLocaleString("es-MX") : ""}
                              </span>
                            </div>
                            <div className="mt-1">{message.text}</div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <label className="form-label fw-semibold">Mensaje de staff</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    maxLength={1000}
                    value={chatStaffText}
                    onChange={(event) => setChatStaffText(event.target.value)}
                    placeholder="Escribe una nota breve para los participantes..."
                  />
                </div>
              </div>
              <div className="modal-footer border border-bottom-0 border-start-0 border-end-0 py-16 px-24">
                <button type="button" className="btn btn-secondary" onClick={() => setChatStaffOpen(false)}>Cerrar</button>
                <button type="button" className="btn btn-primary" onClick={enviarMensajeStaff} disabled={chatStaffSending || !chatStaffText.trim()}>
                  {chatStaffSending ? "Enviando..." : "Enviar mensaje"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default CalendarioPage;
